import { useCallback, useMemo } from 'react';
import {
  Link as MuiLink, Grid, Typography, Stack, IconButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LinkIcon from '@mui/icons-material/Link';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { faSquareFacebook, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { Formik } from 'formik';
import { useImperativeClipboard } from '@client/hooks/useClipboardCopy';
import { buildURIFromParams, getShowCallLink } from '@sda/utils/url';
import { showSnackbar } from '@client/listeners/lib/snackbar';
import { Form, TextField } from '@client/components/forms';
import { Button } from '@client/components/common/Button';
import {
  useDispatch, useInstanceSelector, useListeners, useSelector,
} from '@client/hooks/redux.hooks';
import { startShareBeacon, startShareBeaconLink } from '@client/slices/entities/beacons.reducer';
import { useConnectionSearch } from '@client/hooks/useConnectionSearch';
import { Modal } from '@client/components/globals/Modal';
import { getSessionUserFlat } from '@client/selectors/helpers/session';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import { getBeaconById } from '@client/selectors/entities/beacons';
import { EmailSchema } from './email.schema';
import { shareShowCallListeners } from './share-showcall.listeners';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  linkInput: {
    width: '100%',
  },
  linkIcon: {
    color: theme.palette.grey[500],
    fontSize: 24,
    marginRight: theme.spacing(1 / 2),
    transform: 'rotate(-45deg)',
  },
  shareToolbar: {
    '& > .MuiIconButton-root': {
      marginRight: theme.spacing(1),
    },
  },
}));

interface ShareModalProps {
  readonly beaconId: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({ beaconId }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const beacon = useInstanceSelector(getBeaconById, beaconId);
  const user = useSelector(getSessionUserFlat);

  useListeners(shareShowCallListeners);

  const handleCopySuccess = useCallback(() => {
    showSnackbar('Link copied to clipboard.');

    dispatch(startShareBeaconLink(beaconId));
  }, [dispatch, beaconId]);

  const copyUrl = getShowCallLink(beacon, 'copy');
  const handleCopy = useImperativeClipboard(copyUrl, handleCopySuccess);

  const [{ connectionId }, SearchComponent] = useConnectionSearch();

  const handleEmailSend = useCallback(({ email }: { email: string }) => {
    dispatch(startShareBeacon({ beaconId, email }));
  }, [dispatch, beaconId]);

  const handleConnectionSend = useCallback(() => {
    dispatch(startShareBeacon({ beaconId, connectionId }));
  }, [dispatch, beaconId, connectionId]);

  const facebookUrl = useMemo(() => {
    const baseUrl = 'https://www.facebook.com/sharer/sharer.php';

    return buildURIFromParams(baseUrl, {
      u: getShowCallLink(beacon, 'facebook'),
    });
  }, [beacon]);

  const twitterUrl = useMemo(() => {
    const baseUrl = 'https://twitter.com/intent/tweet';

    return buildURIFromParams(baseUrl, {
      text: '',
      url: getShowCallLink(beacon, 'twitter'),
      via: 'sidedooraccess',
    });
  }, [beacon]);

  const webShareData = useMemo(() => ({
    url: getShowCallLink(beacon, 'web'),
  }), [beacon]);

  const canShare = useMemo(() => {
    if (!navigator.share) {
      return false;
    }

    if (!navigator.canShare) {
      // Certain browsers support web share but not the canShare utility
      // For these browsers, we just have to try sharing anyway
      return true;
    }

    return navigator.canShare(webShareData);
  }, [webShareData]);

  const handleWebShareClick = useCallback(() => {
    if (canShare) {
      void navigator.share(webShareData);
    }
  }, [canShare, webShareData]);

  return (
    <Stack
      spacing={3}
      className={classes.root}
    >
      <div>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
        >
          Social
        </Typography>

        <div className={classes.shareToolbar}>
          <IconButton
            target="_blank"
            href={facebookUrl}
            rel="noreferrer"
            color="inherit"
            size="medium"
            edge="start"
          >
            <FontAwesomeIcon icon={faSquareFacebook} />
          </IconButton>

          <IconButton
            target="_blank"
            href={twitterUrl}
            rel="noreferrer"
            color="inherit"
            size="medium"
          >
            <FontAwesomeIcon icon={faTwitter} />
          </IconButton>

          {Boolean(canShare) && (
            <IconButton
              onClick={handleWebShareClick}
              color="default"
              size="medium_inline"
              edge="end"
            >
              <MoreHorizIcon />
            </IconButton>
          )}
        </div>
      </div>

      <div>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
        >
          Copy Link
        </Typography>

        <div
          className={classes.linkInput}
        >
          <Grid
            container
            spacing={0}
            alignItems="center"
            justifyContent="flex-start"
            wrap="nowrap"
          >
            <Grid item>
              <LinkIcon className={classes.linkIcon} />
            </Grid>

            <Grid
              item
              zeroMinWidth
            >
              <MuiLink
                onClick={handleCopy}
                aria-label="Copy Link"
                variant="subtitle1"
                display="block"
                noWrap
              >
                {copyUrl}
              </MuiLink>
            </Grid>
          </Grid>
        </div>
      </div>

      <Formik
        onSubmit={handleEmailSend}
        initialValues={{ email: '' }}
        validationSchema={EmailSchema}
      >
        {({ values }) => (
          <Form>
            <Stack spacing={2}>
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
              />

              <div>
                <Button
                  scope="send-connect-share-email"
                  onSuccess={() => {
                    Modal.hide();
                    showSnackbar(`Show call shared with ${values.email}.`);
                  }}
                  ButtonProps={{
                    disabled: !values.email,
                  }}
                >
                  Share via Email
                </Button>
              </div>
            </Stack>
          </Form>
        )}
      </Formik>

      {Boolean(user?.hasConnection) && (
        <Stack spacing={1.5}>
          {SearchComponent}

          <div>
            <Button
              id="sda-share-showcall-connection-button"
              scope={`send-connect-share-email${connectionId || 'no-connection'}`}
              onClick={handleConnectionSend}
              onSuccess={() => {
                Modal.hide();
                showSnackbar('Show call shared with your connection.');
              }}
              ButtonProps={{
                disabled: !connectionId,
              }}
            >
              Share
            </Button>
          </div>
        </Stack>
      )}
    </Stack>
  );
};
